import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  DEFAULT_BOOKING_CONFIRMATION,
  bookingConfirmationSettingsSchema,
  normaliseBookingConfirmation,
  type BookingConfirmationSettings,
} from '@/lib/bookingConfirmation';

export const bookingConfirmationKey = (orgId: string | null | undefined) => ['booking-confirmation', orgId] as const;

/**
 * How this business confirms online bookings (company_info, anon-readable so
 * the public booking page can set expectations). Kept as its own small query
 * so a read failure only ever falls back to "I'll approve each one" — it never
 * takes the booking page's currency/payment settings down with it.
 */
export function useBookingConfirmation(orgId: string | null | undefined) {
  return useQuery({
    queryKey: bookingConfirmationKey(orgId),
    enabled: Boolean(orgId),
    queryFn: async (): Promise<BookingConfirmationSettings> => {
      const { data, error } = await supabase
        .from('company_info')
        .select('booking_confirmation_mode, booking_auto_assign')
        .eq('organization_id', orgId!)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        console.error('Could not load booking confirmation setting', error);
        return DEFAULT_BOOKING_CONFIRMATION;
      }
      return normaliseBookingConfirmation(data);
    },
  });
}

export function useSaveBookingConfirmation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ orgId, settings }: { orgId: string; settings: BookingConfirmationSettings }) => {
      const parsed = bookingConfirmationSettingsSchema.parse(settings);
      const { error } = await supabase
        .from('company_info')
        .update({ booking_confirmation_mode: parsed.mode, booking_auto_assign: parsed.autoAssign })
        .eq('organization_id', orgId);
      if (error) throw error;
      return parsed;
    },
    onSuccess: (settings, { orgId }) => {
      queryClient.setQueryData(bookingConfirmationKey(orgId), settings);
    },
  });
}
